import { useEffect, useState, useMemo } from "react";
import { Link } from "react-router-dom";
import { Search } from "lucide-react";
import SEO from "@/components/SEO";
import { PageHero } from "@/components/common/Bits";
import { Reveal } from "@/components/motion/Reveal";
import { SkeletonCard, EmptyState } from "@/components/common/States";
import { useCategories } from "@/hooks/useContent";
import { getProjects } from "@/services/api";

const PAGE_SIZE = 6;

export default function OurWorks() {
    const [projects, setProjects] = useState([]);
    const [loading, setLoading] = useState(true);
    const [cat, setCat] = useState("All");
    const [query, setQuery] = useState("");
    const [visible, setVisible] = useState(PAGE_SIZE);
    const categories = useCategories(projects);

    useEffect(() => {
        let active = true;
        getProjects().then((data) => {
            if (!active) return;
            setProjects(data);
            setLoading(false);
        });
        return () => {
            active = false;
        };
    }, []);

    const filtered = useMemo(() => {
        return projects.filter((p) => {
            const matchesCat = cat === "All" || p.category === cat;
            const q = query.trim().toLowerCase();
            const matchesQuery =
                !q ||
                [p.title, p.location, p.propertyType, p.shortDescription]
                    .filter(Boolean)
                    .some((field) => String(field).toLowerCase().includes(q));
            return matchesCat && matchesQuery;
        });
    }, [projects, cat, query]);

    useEffect(() => setVisible(PAGE_SIZE), [cat, query]);

    const shown = filtered.slice(0, visible);

    return (
        <>
            <SEO
                title="Our Works — Property Project Portfolio Singapore"
                path="/our-works"
                description="Browse ProCare.SG's property projects: moving, cleaning, painting, repairs, renovation, spalling concrete, mould treatment and more."
            />
            <PageHero
                label="Our Works"
                title="Every MOVE, Professionally Cared For"
                subtitle="A look at recent property transformations across HDB flats, condominiums, landed homes and commercial spaces."
                image="/images/painting.webp"
            />

            <section className="py-16">
                <div className="mx-auto max-w-7xl px-6">
                    {/* Filters + search */}
                    <div className="flex flex-col gap-5 lg:flex-row lg:items-center lg:justify-between">
                        <div className="flex flex-wrap gap-2">
                            {categories.map((c) => (
                                <button
                                    key={c}
                                    onClick={() => setCat(c)}
                                    data-testid={`filter-${c.toLowerCase().replace(/\s+/g, "-")}`}
                                    className={`rounded-full px-4 py-2 text-sm font-medium transition-colors ${cat === c ? "bg-brand-red text-white" : "bg-brand-surface text-brand-blue hover:bg-brand-blue/10"}`}
                                >
                                    {c}
                                </button>
                            ))}
                        </div>
                        <div className="relative w-full lg:w-72">
                            <Search className="pointer-events-none absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-brand-ink/60" />
                            <input
                                type="text"
                                placeholder="Search projects…"
                                value={query}
                                onChange={(e) => setQuery(e.target.value)}
                                data-testid="works-search"
                                className="w-full rounded-full border border-brand-blue/15 bg-white py-2.5 pl-11 pr-4 text-sm outline-none focus:border-brand-blue"
                            />
                        </div>
                    </div>

                    {/* Grid */}
                    <div className="mt-12 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
                        {loading ? (
                            [...Array(6)].map((_, i) => <SkeletonCard key={i} />)
                        ) : shown.length ? (
                            shown.map((p, i) => (
                                <Reveal key={p.id} delay={(i % 3) * 0.06}>
                                    <Link to={`/our-works/${p.slug}`} data-testid={`project-${p.slug}`} className="group block overflow-hidden rounded-3xl bg-white shadow-brand transition-all duration-300 hover:-translate-y-1 hover:shadow-brand-lg">
                                        <div className="relative aspect-[4/3] overflow-hidden">
                                            <img src={p.featuredImage} alt={p.title} loading="lazy" className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-110" />
                                            <span className="absolute left-4 top-4 rounded-full bg-brand-red px-3 py-1 text-xs font-bold uppercase tracking-widest text-white">{p.category}</span>
                                            {(p.before && p.after) && (
                                                <span className="absolute right-4 top-4 rounded-full bg-brand-blue/90 px-3 py-1 text-xs font-bold uppercase tracking-widest text-white">Before / After</span>
                                            )}
                                        </div>
                                        <div className="p-5">
                                            <h3 className="font-display text-lg font-semibold text-brand-blue">{p.title}</h3>
                                            <p className="mt-1 text-xs uppercase tracking-widest text-brand-ink/65">{p.propertyType} · {p.location}</p>
                                            <p className="mt-3 text-sm leading-relaxed text-brand-ink/60">{p.shortDescription}</p>
                                        </div>
                                    </Link>
                                </Reveal>
                            ))
                        ) : (
                            <div className="sm:col-span-2 lg:col-span-3">
                                <EmptyState title="No projects found" desc="Try a different category or search term." />
                            </div>
                        )}
                    </div>

                    {!loading && visible < filtered.length && (
                        <div className="mt-12 text-center">
                            <button
                                onClick={() => setVisible((v) => v + PAGE_SIZE)}
                                data-testid="load-more"
                                className="rounded-full border-2 border-brand-blue px-8 py-3 text-sm font-bold text-brand-blue transition-colors hover:bg-brand-blue hover:text-white"
                            >
                                Load More
                            </button>
                        </div>
                    )}
                </div>
            </section>
        </>
    );
}
